import { useCachedImage } from '@/hooks/useCachedImage';
import { useSongFallbackAlbum } from '@/hooks/useSongFallbackAlbum';
import AlbumCover from '@/components/albums/AlbumCover';

interface CoverArtProps {
  songId: number;
  revisionId?: number;
  alt: string;
  className?: string;
  coverArtUrl?: string; // If provided and null/undefined, skip fetching
}

/**
 * Placeholder box shown when there is no artwork (or while it loads), so the
 * cover slot always occupies the same footprint and adjacent titles never
 * shift — matching the desktop app's empty cover box.
 */
function CoverPlaceholder({ className, alt }: { className: string; alt: string }) {
  return <div className={`bg-surface ${className}`} role="img" aria-label={alt} />;
}

/** Renders a song's own cover art (object-URL cached), else a placeholder box. */
function OwnCoverArt({ songId, revisionId, alt, className = '' }: Omit<CoverArtProps, 'coverArtUrl'>) {
  const { imageUrl, isLoading, hasError } = useCachedImage(songId, revisionId, true);

  if (hasError || isLoading || !imageUrl) {
    return <CoverPlaceholder className={className} alt={alt} />;
  }

  return <img src={imageUrl} alt={alt} className={className} />;
}

export default function CoverArt({ songId, revisionId, alt, className = '', coverArtUrl }: CoverArtProps) {
  // If coverArtUrl prop is explicitly provided and is null/empty, don't fetch
  // If not provided (undefined), fetch as normal (backwards compatibility)
  const shouldFetch = coverArtUrl === undefined || !!coverArtUrl;

  // For a song with no own artwork (and not a specific revision), fall back to
  // the first album with artwork the song is assigned to — matching desktop.
  const fallbackAlbum = useSongFallbackAlbum(songId, !shouldFetch && revisionId === undefined);

  if (shouldFetch) {
    return <OwnCoverArt songId={songId} revisionId={revisionId} alt={alt} className={className} />;
  }

  if (fallbackAlbum) {
    return (
      <AlbumCover
        albumId={fallbackAlbum.albumId}
        coverArtUrl={fallbackAlbum.coverArtUrl}
        alt={alt}
        className={className}
      />
    );
  }

  // No own art and no album fallback: render the placeholder box.
  return <CoverPlaceholder className={className} alt={alt} />;
}
